import AIChatFloatingButton from '@cobliteam/ai/src/containers/AIChatFloating/AIChatFloatingButton';
import { useAIAssistant, useAIAssistantStore } from '@cobliteam/ai/src/store/useAIAssistant';
import { useFloatingChat } from '@cobliteam/ai/src/store/useFloatingChat';
import AIUserInviteModal from '@cobliteam/ai/src/ui/AIUserInviteModal';
import { Analytics } from '@cobliteam/analytics';
import { buildUserInfoChanges, fromE164BR, toE164BR } from '@cobliteam/api-lib';
// Common Types
import { Location, locationName } from '@cobliteam/common-types';
// Developer Tools
import DeveloperTools from '@cobliteam/developer-tools';
// Hooks
import useChatWidget from '@cobliteam/hooks/src/useChatWidget';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useFeatureFlags, { type FeatureFlags } from '@cobliteam/hooks/src/useFeatureFlags';
import useIntercom from '@cobliteam/hooks/src/useIntercom';
import useLogout from '@cobliteam/hooks/src/useSession/useLogout';
import useUpdateAccount from '@cobliteam/hooks/src/useSession/useUpdateAccount';
import { lazy } from '@cobliteam/lazy-with-retries';
// Preferences
import Preferences from '@cobliteam/preferences';
// UI Business Components
import AppHeader from '@cobliteam/ui-business/components/AppHeader';
import AppImpersonate from '@cobliteam/ui-business/components/AppImpersonate';
import AppSidebar from '@cobliteam/ui-business/components/AppSidebar';
import { SidebarInset, SidebarProvider } from '@cobliteam/ui-business/components/AppSidebar/components/Sidebar';
import NudgeFloatingStack from '@cobliteam/ui-business/components/Nudge/NudgeFloatingStack';
// React and Router
import { type ReactNode, Suspense, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { NUDGE_LIMIT, NUDGE_SOURCES } from '../../nudges/nudgeSources';
import AppContentLoadingBoundary from './AppContentLoadingBoundary';

// AIAssistantSidepanel owns the Mastra chat client and its markdown/shiki renderer stack.
// Keep it out of the entry chunk — mount only after the user opens the assistant for the
// first time; the Zustand store keeps its state so subsequent closes/opens are cheap.
const AIAssistantSidepanel = lazy(() => import('@cobliteam/ai/src/containers/AIAssistant/AIAssistantSidepanel'));

// Local Hooks and Utils
import useRedirectAfterLogin from '../../hooks/useRedirectAfterLogin';
import { initializeThirdParty } from '../../libs/configureThirdParty';

function AISidepanelStateSync({ pathname, hide, setPosition }: { pathname: string; hide: (value: boolean) => void; setPosition: (position: 'default' | 'tab') => void }) {
  const { isAISidepanelOpen } = useAIAssistant();

  useEffect(() => {
    const hasDefaultTab = pathname === Location.MONITORING || pathname === Location.TRACKING || pathname === Location.GEOFENCES_CREATE || pathname.startsWith('/geofences/edit/');

    if (isAISidepanelOpen) {
      hide(true);
    } else {
      hide(false);
      setPosition(hasDefaultTab ? 'tab' : 'default');
    }

    return () => {
      hide(false);
    };
  }, [pathname, setPosition, hide, isAISidepanelOpen]);

  return null;
}

function AppLayout({ children }: { children: ReactNode }) {
  const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';
  const { mutate: logout } = useLogout();
  const { mutate: updateAccount } = useUpdateAccount();
  const { setPosition, hide } = useChatWidget();
  const setIntercomOpen = useFloatingChat((s) => s.setIntercomOpen);
  const isAISidepanelOpen = useAIAssistantStore((s) => s.isAISidepanelOpen);
  // Track first-ever open so we keep the sidepanel mounted once loaded (preserves chat state).
  const hasEverOpenedAssistantRef = useRef(false);
  if (isAISidepanelOpen) hasEverOpenedAssistantRef.current = true;
  const { ddRumSampleRate, ddRumReplaySampleRate, oneSignal, useDdRum, sidebar, aiChat, register, unregister, aiUserModalUpdate } = useFeatureFlags();
  const history = useHistory();
  const [isAIUserInviteModalOpen, setIsAIUserInviteModalOpen] = useState(true);
  const currentLocation = locationName[history.location.pathname as Location];
  const { getSession, useGetUserData } = useUser();
  const { redirectSessionStorage } = useRedirectAfterLogin();
  const session = getSession();
  const { data: userData } = useGetUserData();
  const currentFleet = session?.fleet;
  const fleets = userData?.userFleets;
  const hasRegisteredPhoneNumber = Boolean(session.user.phoneNumber?.trim());
  // Frota inativa: os elementos flutuantes de IA apontam para APIs bloqueadas na sessão
  // restrita (403), então não montam — mesmo racional dos controles escondidos no AppHeader.
  const isInactiveFleet = session?.fleet.active === false;
  // O gate por isInactiveFleet cobre também o sidepanel já aberto numa frota ativa: se a sessão
  // virar inativa sem remount completo, ele desmonta em vez de seguir chamando a API bloqueada.
  const shouldMountAssistant = !(isDemoMode || isInactiveFleet) && hasEverOpenedAssistantRef.current;
  const shouldShowAIUserInviteModal = !isDemoMode && aiChat && aiUserModalUpdate && !hasRegisteredPhoneNumber && !isInactiveFleet;

  const { build: intercomBuild } = useIntercom({
    onClose: () => {
      hide(false);
      setIntercomOpen(false);
    },
  });

  const handleBetaOptInChange = async (enabled: boolean) => {
    if (enabled) {
      await register('userEarlyAccess');
    } else {
      await unregister('userEarlyAccess');
    }
  };

  const handleInvitePhoneNumberUpdate = (phoneNumber: string) => {
    const initialPhoneE164 = session.user.phoneNumber ?? '';
    const nextPhoneE164 = toE164BR(phoneNumber);
    const changes = buildUserInfoChanges({
      initialName: session.user.name,
      initialPhone: initialPhoneE164,
      nextName: session.user.name,
      nextPhone: nextPhoneE164,
    });

    if (!('phone_number' in changes)) return;

    updateAccount({ changes });
  };

  // Initialize core services
  useEffect(() => {
    Analytics.initialize();
    Preferences.initialize();
  }, []);

  // Handle redirect after login
  useEffect(() => {
    const redirectUrl = redirectSessionStorage();
    if (redirectUrl) {
      history.push(redirectUrl);
    }
  }, [history, redirectSessionStorage]);

  // Initialize third-party services
  useEffect(() => {
    const featureFlags = {
      ddRumSampleRate,
      ddRumReplaySampleRate,
      oneSignal,
      useDdRum,
      sidebar,
      aiChat,
      aiUserModalUpdate,
    } as FeatureFlags;
    if (!isDemoMode) {
      intercomBuild();
    }
    initializeThirdParty({ session, userData, featureFlags });
  }, [session, userData, ddRumSampleRate, ddRumReplaySampleRate, oneSignal, useDdRum, sidebar, aiChat, aiUserModalUpdate, intercomBuild, isDemoMode]);

  // Track page views
  useEffect(() => {
    if (!session) return;
    Analytics.pageview(currentLocation, { fleetId: session.fleet.id });
  }, [currentLocation, session]);

  return (
    <AppImpersonate>
      <SidebarProvider>
        <AppSidebar fleets={fleets || []} currentFleet={currentFleet} />
        <SidebarInset>
          <AppHeader user={session.user} logout={() => logout()} />
          <div className='flex h-screen-without-header overflow-hidden'>
            <section id='app-body' data-testid='app-body' className='relative flex-1 overflow-y-auto bg-gray-2'>
              <AppContentLoadingBoundary>{children}</AppContentLoadingBoundary>
              <DeveloperTools />
              {!(isDemoMode || isInactiveFleet) && <NudgeFloatingStack sources={NUDGE_SOURCES} limit={NUDGE_LIMIT} />}
              {!(isDemoMode || isInactiveFleet) && <AIChatFloatingButton />}
              {shouldShowAIUserInviteModal && (
                <AIUserInviteModal
                  fleetId={session.fleet.id}
                  userId={session.user.id}
                  initialPhoneNumber={fromE164BR(session.user.phoneNumber ?? '')}
                  open={isAIUserInviteModalOpen}
                  onOpenChange={setIsAIUserInviteModalOpen}
                  onBetaOptInChange={handleBetaOptInChange}
                  onPhoneNumberUpdate={handleInvitePhoneNumberUpdate}
                />
              )}
            </section>
            {!(isDemoMode || isInactiveFleet) && <AISidepanelStateSync pathname={history.location.pathname} hide={hide} setPosition={setPosition} />}
            {shouldMountAssistant && (
              <Suspense fallback={null}>
                <AIAssistantSidepanel />
              </Suspense>
            )}
          </div>
        </SidebarInset>
      </SidebarProvider>
    </AppImpersonate>
  );
}

export default AppLayout;
